import { getFreshAccessToken } from "../pages/chat/api";

/* Wait for a background worker to finish, over /ws/upload-status/.
 *
 * Lifted out of ProposalPage, which defined it locally, because Sensa's
 * proposal flow now waits on the same workers. A second hand-rolled WebSocket
 * lifecycle is a second place to get reconnection, timeouts and cleanup subtly
 * different — and the way that shows up is a progress view that never finishes
 * on one screen and does on the other.
 *
 * Behaviour worth knowing before using it:
 *
 *   IT RESOLVES TRUE ON ALMOST EVERYTHING. Socket error, close and timeout all
 *   resolve `true`, not false. The socket is how progress is WATCHED, not how
 *   work is confirmed — the worker keeps running whatever happens to this
 *   connection, so a dropped socket must not be reported as a failed job. Only
 *   an explicit error frame resolves false.
 *
 *   FRAMES ARE FILTERED BY statusKey. The channel is shared by every upload and
 *   every analysis for this user, so without the filter one project's progress
 *   drives another project's bar.
 *
 * Returns { promise, cancel } — call cancel() on unmount, or the socket outlives
 * the component that opened it.
 */
export const waitForUploadStatus = ({ statusKey, timeoutMs, onProgress, onStream, onError }) => {
  let settled = false;
  let ws = null;
  let timer = null;
  let resolveWait;
  const promise = new Promise((resolve) => {
    resolveWait = resolve;
  });

  const finish = (ok) => {
    if (settled) return;
    settled = true;
    if (timer) clearTimeout(timer);
    if (ws) {
      try {
        ws.close();
      } catch (e) {
        /* noop */
      }
    }
    resolveWait(ok);
  };

  (async () => {
    try {
      const freshToken = await getFreshAccessToken();
      ws = new WebSocket(
        `${process.env.REACT_APP_WS_URL || "ws://localhost:8000"}/ws/upload-status/?token=${freshToken}`
      );
      timer = setTimeout(() => finish(true), timeoutMs);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (statusKey && data.pdf_key && data.pdf_key !== statusKey) return;
          if (data.type === "progress") {
            onProgress?.(data);
          } else if (data.type === "stream") {
            // Drafting text as it is written. Optional: uploads and analyses
            // never send these, and a caller that only wants a percentage does
            // not have to know they exist.
            const delta = data.metadata?.delta || "";
            if (delta) onStream?.(delta, data);
          } else if (data.type === "completed") {
            onProgress?.(data);
            finish(true);
          } else if (data.type === "error" || data.error) {
            onError?.(data);
            finish(false);
          }
        } catch (e) {
          /* ignore malformed frames */
        }
      };
      ws.onerror = () => finish(true);
      ws.onclose = () => finish(true);
    } catch (e) {
      finish(true);
    }
  })();

  return { promise, cancel: () => finish(true) };
};

export default waitForUploadStatus;
